import { createSignal } from "solid-js";
import type { ChatViewContext, ConversationId, ProjectId } from "../api/types";

/**
 * Module-level signal for the chat drawer's open/closed state.
 *
 * Lives outside any component so it survives route transitions —
 * AppShell is mounted per-route in App.tsx, so a local signal would
 * reset when navigating between pages.
 */
const [chatDrawerOpen, setChatDrawerOpen] = createSignal(false);

/**
 * Identity of the view the chat is about. Opening the drawer in a new context
 * starts a fresh chat; opening it again in the same one resumes the chat
 * already in progress there (FLOW-696).
 *
 * The backlog collapses to a single key: its filters and visible items change
 * as the user works, and a new chat per keystroke is not a new context. The
 * project is part of every key so switching projects never resumes a
 * conversation belonging to the previous one.
 */
export function chatContextKey(
  projectId: ProjectId,
  context: ChatViewContext,
): string {
  switch (context.type) {
    case "work_item":
      return `${projectId}:work_item:${context.workItemId}`;
    case "backlog":
      return `${projectId}:backlog`;
    default:
      return `${projectId}:general`;
  }
}

/**
 * The conversation the drawer is holding in each context it has been opened
 * in, keyed by `chatContextKey`. A null value is a chat that has been started
 * but has no message yet, which is distinct from a context that is absent here
 * and so gets a new chat.
 *
 * One entry per context, not one slot: leaving a view and coming back to it is
 * ordinary navigation, and a single slot would hand the user a duplicate chat
 * every time they did it — the thing FLOW-696 is asking to avoid.
 */
const [chatSessions, setChatSessions] = createSignal<
  Record<string, ConversationId | null>
>({});

/** Record which conversation a context is chatting in. */
export function rememberChatSession(
  key: string,
  conversationId: ConversationId | null,
): void {
  setChatSessions((prev) =>
    key in prev && prev[key] === conversationId
      ? prev
      : { ...prev, [key]: conversationId },
  );
}

export { chatDrawerOpen, setChatDrawerOpen, chatSessions, setChatSessions };
